<template>
  <div
    class="icon-area"
    role="button"
    tabindex="0"
    @click="onClick"
    @keydown.enter="onClick"
    @keydown.space.prevent="onClick"
  >
    <div class="icon-wrap">
      <icon-image :iconUrl="iconUrl" />
    </div>
    <img v-if="iconFrame" class="icon-frame" :src="iconFrame" />
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Emit } from 'vue-property-decorator'
import type { AgencyEventStreamerUser } from '@/models/StreamerUser'
import { BroadcastingMethods } from '@/models/Media'
import { Client, defaultNativeInterfaceClient } from '@/utils/NativeInterface'
import IconImage from '@/components/IconImage.vue'
import ImgUrl from '@/utils/ImgUrl'
import type { UserCurrentStatus } from '@/api/pb/Common/User/UserCurrentStatus'

@Component({
  components: {
    IconImage,
  },
})
export default class AgencyEventRankingStreamerIcon extends Vue {
  @Prop({ required: true })
  streamer!: AgencyEventStreamerUser

  @Prop({ required: true })
  currentStatus!: UserCurrentStatus | null | undefined

  @Prop({ default: 48 })
  iconSize!: number

  @Emit('firebaseEvent')
  firebaseEvent(stateType: string | null) {
    return stateType
  }

  get isStreaming(): boolean {
    if (!this.currentStatus) {
      return false
    }
    return !!this.currentStatus.streaming
  }

  get iconFrame() {
    if (!this.isStreaming) {
      return null
    }

    const isChrome = navigator.userAgent.indexOf('Chrome') >= 0
    return isChrome
      ? '/img/onLiveFrameAnimation.webp'
      : '/img/onLiveFrameAnimation.png'
  }

  get iconUrl(): string {
    if (!this.streamer) return ''
    return ImgUrl.getIconUrlWithSize(this.streamer.iconUrl, this.iconSize) || ''
  }

  onClick() {
    const nativeInterface = defaultNativeInterfaceClient()
    if (nativeInterface.client === Client.native) {
      // webのプロフィールページに遷移する
      this.firebaseEvent(null)
      location.href = `/profile/${this.streamer.profileId}`
      return
    }

    try {
      if (!this.isStreaming) {
        // 配信中でない場合はプロフィールをnativeで開く
        this.firebaseEvent(null)
        nativeInterface.openProfile(this.streamer.profileId)
        return
      }

      // 配信中の場合は当該配信をnativeで開く
      const mediaId = this.currentStatus!.streaming!.mediaId
      this.firebaseEvent('live')
      // protobuf の型に RTMP / ClientRendering の情報がないので ClientRendering をデフォルトとする
      nativeInterface.openLive(mediaId, BroadcastingMethods.ClientRendering)
    } catch {
      // 失敗したらwebでプロフィールを開く
      location.href = `/profile/${this.streamer.profileId}`
    }
  }
}
</script>

<style scoped>
.icon-area {
  display: flex;
  align-items: center;
  width: 52px;
  cursor: pointer;
  position: relative;
}

.icon-wrap {
  display: flex;
  width: 52px;
  padding: 0 4px;
}

.icon-frame {
  width: 52px;
  height: 52px;
  position: absolute;
}
</style>
